<!--
    Locator Sidebar
    ===============
    Layout: flex row (sidebar-container)
      - .sidebar         — the main vertical icon strip (always visible)
      - .draw-controls   — secondary action panel that appears beside the sidebar when the pencil tool is active

    Active tool state is tracked by `activeTool` signal in the TS.
    Clicking a tool button toggles it on/off (same tool click = deselect).
-->
<div class="sidebar-container" (wheel)="$event.stopPropagation()">
    <!-- ===================== Main Sidebar Strip =====================
         Contains two sections:
           - sidebar__top    : primary tool selectors (location, map, pencil)
           - sidebar__bottom : map view controls (zoom in/out, expand, rotate)
    ================================================================= -->
    <div class="sidebar">
        <!-- Primary Tool Selectors
             Each button sets `activeTool` to its key.
             Active button = green background + white icon.
             Inactive button = white background + green icon. -->
        <div class="sidebar__top">
            <!-- Location Pin: future use for placing/searching a location -->
            <button
                class="sidebar__btn"
                [class.sidebar__btn--primary]="activeTool() === 'location'"
                [class.sidebar__btn--outline]="activeTool() !== 'location'"
                (click)="selectTool('location')">
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 14 14"
                    class="sidebar__icon"
                    [class.sidebar__icon--white]="activeTool() === 'location'"
                    [class.sidebar__icon--accent]="activeTool() !== 'location'">
                    <use href="assets/icons/icons-sprite.svg#location-pin"></use>
                </svg>
            </button>

            <!-- Map: future use for map-level options/layers -->
            <button
                class="sidebar__btn"
                [class.sidebar__btn--primary]="activeTool() === 'map'"
                [class.sidebar__btn--outline]="activeTool() !== 'map'"
                (click)="selectTool('map')">
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 16 16"
                    class="sidebar__icon"
                    [class.sidebar__icon--white]="activeTool() === 'map'"
                    [class.sidebar__icon--accent]="activeTool() !== 'map'">
                    <use href="assets/icons/icons-sprite.svg#map-folded"></use>
                </svg>
            </button>

            <!-- Pencil: toggles the draw-controls panel (view, draw, delete, undo, redo) -->
            <button
                class="sidebar__btn"
                [class.sidebar__btn--primary]="activeTool() === 'pencil'"
                [class.sidebar__btn--outline]="activeTool() !== 'pencil'"
                (click)="selectTool('pencil')">
                <svg
                    class="sidebar__icon"
                    [class.sidebar__icon--white]="activeTool() === 'pencil'"
                    [class.sidebar__icon--accent]="activeTool() !== 'pencil'">
                    <use xlink:href="assets/icons/icons-sprite.svg#pencil-edit"></use>
                </svg>
            </button>
        </div>

        <!-- Map View Controls
             Fixed utility buttons at the bottom of the sidebar.
             No active state — these are stateless actions (not yet wired up). -->
        <div class="sidebar__bottom">
            <div class="sidebar__divider"></div>

            <!-- Zoom In -->
            <button
                class="sidebar__btn sidebar__btn--outline sidebar__btn--small"
                (click)="mapControlsService.zoomIn()">
                <svg
                    class="sidebar__icon sidebar__icon--accent sidebar__icon--small"
                    viewBox="0 0 448 512">
                    <path
                        d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z" />
                </svg>
            </button>

            <!-- Zoom Out -->
            <button
                class="sidebar__btn sidebar__btn--outline sidebar__btn--small"
                (click)="mapControlsService.zoomOut()">
                <svg
                    class="sidebar__icon sidebar__icon--accent sidebar__icon--small"
                    viewBox="0 0 448 512">
                    <path
                        d="M432 256c0 17.7-14.3 32-32 32L48 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l352 0c17.7 0 32 14.3 32 32z" />
                </svg>
            </button>

            <!-- Expand / Focus to bounds -->
            <button class="sidebar__btn sidebar__btn--outline sidebar__btn--small">
                <svg
                    class="sidebar__icon sidebar__icon--accent sidebar__icon--small"
                    viewBox="0 0 448 512">
                    <path
                        d="M32 32C14.3 32 0 46.3 0 64v96c0 17.7 14.3 32 32 32s32-14.3 32-32V96h64c17.7 0 32-14.3 32-32s-14.3-32-32-32H32zM64 352c0-17.7-14.3-32-32-32s-32 14.3-32 32v96c0 17.7 14.3 32 32 32h96c17.7 0 32-14.3 32-32s-14.3-32-32-32H64V352zM320 32c-17.7 0-32 14.3-32 32s14.3 32 32 32h64v64c0 17.7 14.3 32 32 32s32-14.3 32-32V64c0-17.7-14.3-32-32-32H320zM448 352c0-17.7-14.3-32-32-32s-32 14.3-32 32v64H320c-17.7 0-32 14.3-32 32s14.3 32 32 32h96c17.7 0 32-14.3 32-32V352z" />
                </svg>
            </button>

            <!-- Rotate / Reset bearing -->
            <button
                class="sidebar__btn sidebar__btn--outline sidebar__btn--small"
                (click)="mapControlsService.reset()">
                <svg
                    class="sidebar__icon sidebar__icon--accent sidebar__icon--small"
                    viewBox="0 0 512 512">
                    <path
                        d="M142.9 142.9c62.2-62.2 162.7-62.5 225.3-1L327 183c-6.9 6.9-8.9 17.2-5.2 26.2s12.5 14.8 22.2 14.8H463.5c0 0 0 0 0 0H472c13.3 0 24-10.7 24-24V72c0-9.7-5.8-18.5-14.8-22.2s-19.3-1.7-26.2 5.2L413.4 96.6c-87.6-86.5-228.7-86.2-315.8 1C73.2 122 55.6 150.7 44.8 181.4c-5.9 16.7 2.9 34.9 19.5 40.8s34.9-2.9 40.8-19.5c7.7-21.8 20.2-42.3 37.8-59.8zM16 312v7.6 .7V440c0 9.7 5.8 18.5 14.8 22.2s19.3 1.7 26.2-5.2l41.6-41.6c87.6 86.5 228.7 86.2 315.8-1c24.4-24.4 42-53.1 52.8-83.8c5.9-16.7-2.9-34.9-19.5-40.8s-34.9 2.9-40.8 19.5c-7.7 21.8-20.2 42.3-37.8 59.8c-62.2 62.2-162.7 62.5-225.3 1L185 329c6.9-6.9 8.9-17.2 5.2-26.2s-12.5-14.8-22.2-14.8H48.4h-.7H40c-13.3 0-24 10.7-24 24z" />
                </svg>
            </button>
        </div>
    </div>

    <!-- Drawer: shown for location tool -->
    @if (activeTool() === 'location') {
        <div class="drawer">
            <h2 class="drawer__title">Location</h2>

            <!-- Search row: autocomplete + filter button -->
            <div class="drawer__search-row">
                <ntv-autocomplete
                    class="drawer__searchbar"
                    [options]="searchOptions()"
                    [config]="{
                        placeholder: searchPlaceholder(),
                        searchable: true,
                        clearable: true,
                    }" />

                <app-filter-dropdown
                    [dropdownOptions]="filterOptions"
                    [showLabel]="false"
                    (optionSelected)="onFilterSelected($event)" />
            </div>

            <div class="drawer__content">There is no data yet.</div>
        </div>
    }

    <!-- Drawer: shown for map tool — territory search + list -->
    @if (activeTool() === 'map') {
        <div class="drawer">
            <h2 class="drawer__title">Search</h2>

            <!-- Search row: searchbar only -->
            <div class="drawer__search-row">
                <app-searchbar
                    class="drawer__searchbar"
                    [placeholder]="'Search territory...'"
                    [minCharacters]="1"
                    [hideOuterSearchButton]="true"
                    [showClearButton]="true" />
            </div>

            <!-- Territory list -->
            <div class="drawer__list">
                <app-territory-list />
            </div>
        </div>
    }

    <!-- ===================== Draw Controls Panel =====================
         Shown only when activeTool === 'pencil'.
         Appears as a secondary column to the right of the sidebar.
         Active control state tracked by `activeDrawControl` signal.
         Buttons communicate with the territory map via MapControlsService.  

         Controls:
           - View   : switches map to view-only mode (no drawing)
           - Draw   : enables polygon drawing on the map
           - Delete : clears all drawn markers/polygon (stateless action)
           - Undo   : removes the last placed marker (stateless action)
           - Redo   : re-adds the last undone marker (stateless action)
    ================================================================= -->

    @if (activeTool() === 'pencil') {
        <div class="draw-controls">
            <!-- View: disables drawing, map becomes pan/zoom only -->
            <button
                class="sidebar__btn"
                [class.sidebar__btn--primary]="activeDrawControl() === 'view'"
                [class.sidebar__btn--dark]="activeDrawControl() !== 'view'"
                title="View"
                (click)="selectDrawControl('view')">
                <svg class="sidebar__icon sidebar__icon--white">
                    <use xlink:href="assets/icons/icons-sprite.svg#paper-plane"></use>
                </svg>
            </button>

            <!-- Draw: enables click-to-place marker mode on the territory map -->
            <button
                class="sidebar__btn"
                [class.sidebar__btn--primary]="activeDrawControl() === 'draw'"
                [class.sidebar__btn--dark]="activeDrawControl() !== 'draw'"
                title="Draw"
                (click)="selectDrawControl('draw')">
                <svg class="sidebar__icon sidebar__icon--white">
                    <use xlink:href="assets/icons/icons-sprite.svg#pencil-edit"></use>
                </svg>
            </button>

            <!-- Merge: merge territories -->
            <button
                class="sidebar__btn sidebar__btn--dark"
                title="Merge"
                (click)="selectDrawControl('merge')">
                <svg class="sidebar__icon sidebar__icon--white">
                    <use xlink:href="assets/icons/icons-sprite.svg#merge"></use>
                </svg>
            </button>

            <!-- Delete: clears all markers and resets the drawing -->
            <button
                class="sidebar__btn sidebar__btn--danger"
                title="Delete"
                [disabled]="territoryMapService.markerCount() === 0"
                (click)="selectDrawControl('delete')">
                <svg class="sidebar__icon sidebar__icon--white">
                    <use xlink:href="assets/icons/icons-sprite.svg#outline-trash"></use>
                </svg>
            </button>

            <div class="draw-controls__divider"></div>

            <!-- Undo: removes the last placed marker -->
            <button
                class="sidebar__btn sidebar__btn--dark"
                title="Undo"
                [disabled]="territoryMapService.markerCount() === 0"
                (click)="selectDrawControl('undo')">
                <svg class="sidebar__icon sidebar__icon--white">
                    <use xlink:href="assets/icons/icons-sprite.svg#undo"></use>
                </svg>
            </button>

            <!-- Redo: re-adds the last undone marker -->
            <button
                class="sidebar__btn sidebar__btn--dark"
                title="Redo"
                [disabled]="territoryMapService.markerCount() === 0"
                (click)="selectDrawControl('redo')">
                <svg class="sidebar__icon sidebar__icon--white">
                    <use xlink:href="assets/icons/icons-sprite.svg#redo"></use>
                </svg>
            </button>
        </div>
    }
</div>
